/**
 * JURO header scroll state
 * ------------------------------------------------------------------
 * Sets data-juro-scrolled on #header-group once the page has scrolled past a
 * threshold. All visual change lives in juro-header.css; this only flips the
 * flag.
 *
 * Deliberately standalone rather than importing the shared scroll engine.
 * Routed through the importmap it proved unreliable here — the header is
 * rendered and morphed by Horizon early in page life, and the import graph
 * resolved inconsistently across loads. One listener for one attribute is a
 * fair trade for something that must work on every page, every time.
 *
 * Also sets data-juro-header-ready on <html>, so CSS can tell whether this
 * script is alive rather than assuming it.
 */

(() => {
  const THRESHOLD = 40;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  let group = null;
  let scrolled = null;
  let ticking = false;

  function apply() {
    ticking = false;
    if (!group) return;
    const next = window.scrollY > THRESHOLD;
    // Only touch the DOM on a real change: this runs on every scroll frame,
    // and a redundant attribute write invalidates style for the subtree.
    if (next === scrolled) return;
    scrolled = next;
    group.toggleAttribute('data-juro-scrolled', next);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(apply);
  }

  /*
   * Publish the header's real occupied height as --juro-header-block.
   *
   * On overlay templates the stylesheet pulls the first section up by this
   * amount so a full-bleed hero runs to the top of the viewport with the bar
   * floating over it. Measured rather than hardcoded because the bar's height
   * changes with the logo size, which is itself viewport-dependent.
   *
   * #header-group cannot be measured directly — Horizon gives it
   * `display: contents`, so it has no box of its own. The header component
   * inside it is the thing that occupies space.
   */
  function publishHeight() {
    const bar = document.querySelector('header-component');
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const styles = getComputedStyle(bar);
    const total = rect.height + parseFloat(styles.marginTop || 0) + parseFloat(styles.marginBottom || 0);
    if (total > 0) {
      document.documentElement.style.setProperty('--juro-header-block', Math.round(total) + 'px');
    }
  }

  function start() {
    group = document.getElementById('header-group');
    if (!group) return false;

    document.documentElement.setAttribute('data-juro-header-ready', '');

    publishHeight();
    window.addEventListener('resize', publishHeight, { passive: true });
    // The logo scales with the viewport, so the bar's height settles only
    // once webfonts and the logo image have loaded.
    window.addEventListener('load', publishHeight, { once: true });
    if (document.fonts?.ready) document.fonts.ready.then(publishHeight);

    // Reduced motion still gets the state change — what it must not get is
    // the transition, which the stylesheet disables separately.
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    reduced.addEventListener?.('change', apply);

    // Restored scroll position on reload means the correct state may be
    // needed before any scroll event fires.
    apply();
    return true;
  }

  function boot() {
    if (start()) return;
    // The header markup can arrive after this script on some navigations.
    // Watch for it rather than assuming a single moment of readiness.
    const observer = new MutationObserver(() => {
      if (start()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    // Don't watch forever if something is genuinely wrong.
    setTimeout(() => observer.disconnect(), 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
